import * as Sentry from '@sentry/react';
import { Controller, useForm } from 'react-hook-form';

import { useCreateDevice, useIntl } from '../../hooks';
import { zodResolver } from '../../lib/zod-resolver.js';
import { DeviceType } from '../../types/device-type.js';
import {
  DeviceFamily,
  deviceFormSchema,
  type DeviceFormValues,
} from '../../validations/device-form.js';
import { Button, DialogFooter, Input, Label } from '../ui';

interface NewDeviceFormProps {
  onSuccess: (serial: string, token: string) => void;
  onCancel: () => void;
}

export default function NewDeviceForm({ onSuccess, onCancel }: NewDeviceFormProps) {
  const intl = useIntl();
  const createDeviceMutation = useCreateDevice();

  const {
    register,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    control,
    watch,
    formState: { errors, isValid },
  } = useForm<DeviceFormValues>({
    resolver: zodResolver(deviceFormSchema),
    defaultValues: {
      serialNumber: '',
      model: DeviceFamily.KVITKA,
      deviceType: DeviceType.DETECTOR_SUPPRESSOR,
    },
    mode: 'onChange',
  });

  const selectedModel = watch('model');

  const onSubmit = async ({ serialNumber, model, deviceType }: DeviceFormValues) => {
    clearErrors('root');

    try {
      const result = await createDeviceMutation.mutateAsync({
        serial: serialNumber,
        model,
        deviceType: model === 'kvitka' ? deviceType : undefined,
      });
      onSuccess(serialNumber, result.token);
      reset();
    } catch (error) {
      Sentry.captureException(error);
      console.error('Failed to create device:', error);
      setError('root', {
        type: 'server',
        message: intl.formatMessage({ id: 'newDeviceForm.createError' }),
      });
    }
  };

  return (
    <form id="add-device-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="serialNumber">
          {intl.formatMessage({ id: 'newDeviceForm.serialNumber' })} *
        </Label>
        <Input
          id="serialNumber"
          type="text"
          placeholder={intl.formatMessage({ id: 'newDeviceForm.serialNumberPlaceholder' })}
          {...register('serialNumber')}
        />
        {errors.serialNumber && (
          <p className="text-sm text-destructive">{errors.serialNumber.message}</p>
        )}
      </div>
      <div className="space-y-2">
        <Label>{intl.formatMessage({ id: 'newDeviceForm.deviceFamily' })} *</Label>
        <Controller
          name="model"
          control={control}
          render={({ field }) => (
            <div className="flex gap-3">
              {Object.values(DeviceFamily).map((family) => {
                const isSelected = field.value === family;

                return (
                  <label
                    className={`flex cursor-pointer select-none items-center gap-3 rounded-lg border px-4 py-3 transition-colors flex-1 ${
                      isSelected
                        ? 'border-black bg-background-dark'
                        : 'border-border hover:bg-background-light'
                    }`}
                    key={`device-family-${family}`}
                  >
                    <input
                      type="radio"
                      value={family}
                      checked={isSelected}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="sr-only"
                    />
                    <div
                      className={`flex h-4 w-4 items-center justify-center rounded-full border ${
                        isSelected ? 'border-black bg-black' : 'border-border'
                      }`}
                    >
                      {isSelected && <div className="h-2 w-2 rounded-full bg-background-light" />}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-sm">
                        {intl.formatMessage({
                          id: `newDeviceForm.family${family.charAt(0).toUpperCase() + family.slice(1)}`,
                        })}
                      </div>
                      <div className="text-xs text-foreground">
                        {intl.formatMessage({
                          id: `newDeviceForm.family${family.charAt(0).toUpperCase() + family.slice(1)}Description`,
                        })}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        />
      </div>
      {selectedModel === 'kvitka' && (
        <div className="space-y-2">
          <Label>{intl.formatMessage({ id: 'newDeviceForm.deviceType' })} *</Label>
          <Controller
            name="deviceType"
            control={control}
            render={({ field }) => (
              <div className="flex gap-3">
                {Object.values(DeviceType).map((type) => {
                  const isSelected = field.value === type;

                  return (
                    <label
                      className={`flex cursor-pointer select-none items-center gap-3 rounded-lg border px-4 py-3 transition-colors flex-1 ${
                        isSelected
                          ? 'border-black bg-background-dark'
                          : 'border-border hover:bg-background-light'
                      }`}
                      key={`device-type-${type}`}
                    >
                      <input
                        type="radio"
                        value={type}
                        checked={isSelected}
                        onChange={(e) => field.onChange(e.target.value)}
                        className="sr-only"
                      />
                      <div
                        className={`flex h-4 w-4 items-center justify-center rounded-full border ${
                          isSelected ? 'border-black bg-black' : 'border-border'
                        }`}
                      >
                        {isSelected && <div className="h-2 w-2 rounded-full bg-background-light" />}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-sm">
                          {intl.formatMessage({
                            id: `deviceSettings.fields.deviceType.${type}.label`,
                          })}
                        </div>
                        <div className="text-xs text-foreground">
                          {intl.formatMessage({
                            id: `deviceSettings.fields.deviceType.${type}.description`,
                          })}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          />
          {errors.deviceType && (
            <p className="text-sm text-destructive">{errors.deviceType.message}</p>
          )}
        </div>
      )}
      {errors.root && <p className="text-sm text-destructive">{errors.root.message}</p>}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          {intl.formatMessage({ id: 'newDeviceForm.cancel' })}
        </Button>
        <Button type="submit" disabled={createDeviceMutation.isPending || !isValid}>
          {createDeviceMutation.isPending
            ? intl.formatMessage({ id: 'newDeviceForm.creating' })
            : intl.formatMessage({ id: 'newDeviceForm.createDevice' })}
        </Button>
      </DialogFooter>
    </form>
  );
}
